import type { Icon } from '@phosphor-icons/react';
import type { Severity } from '@regia/shared';
import type { ReactNode } from 'react';
import { cn } from './cn';

const severityStyle: Record<Severity | 'neutral', string> = {
  neutral: 'bg-ink/7 text-ink-2',
  info: 'bg-action-soft text-action',
  warn: 'bg-warn-soft text-warn',
  alert: 'bg-alert-soft text-alert',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: Severity | 'neutral';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-[0.8125rem] font-medium whitespace-nowrap',
        severityStyle[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function ColorDot({
  color,
  size = 10,
  className,
}: {
  color: string;
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn('inline-block shrink-0 rounded-full', className)}
      style={{ width: size, height: size, backgroundColor: color }}
    />
  );
}

export function Avatar({
  name,
  size = 36,
  className,
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-ink text-sheet t-title',
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials || '·'}
    </span>
  );
}

export interface EmptyStateProps {
  icon?: Icon;
  title: string;
  message?: string;
  action?: ReactNode;
  className?: string;
}

/** Stato vuoto: una frase che invita ad agire, più l'azione (spec 6.6). */
export function EmptyState({ icon: IconCmp, title, message, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-[var(--radius-sheet)] border border-dashed border-line px-5 py-7',
        className,
      )}
    >
      {IconCmp ? <IconCmp size={28} className="text-ink-3" aria-hidden /> : null}
      <div>
        <p className="t-title text-lg text-ink">{title}</p>
        {message ? <p className="mt-1 max-w-prose text-[0.9375rem] text-ink-2">{message}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-lg bg-ink/7', className)} />;
}

export function ProgressBar({
  value,
  className,
  label,
}: {
  value: number;
  className?: string;
  label?: string;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-ink/8', className)}
    >
      <div
        className="h-full rounded-full bg-action transition-[width] duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export interface StatProps {
  label: string;
  value: string;
  delta?: number;
  /** true se una crescita è un male (es. tempo di risposta). */
  invert?: boolean;
  className?: string;
}

/** Numero grande con etichetta e variazione percentuale. */
export function Stat({ label, value, delta, invert, className }: StatProps) {
  const good = delta === undefined ? null : invert ? delta < 0 : delta > 0;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="text-sm text-ink-2">{label}</span>
      <span className="tabular t-title text-2xl text-ink">{value}</span>
      {delta !== undefined && Number.isFinite(delta) ? (
        <span
          className={cn(
            'tabular text-sm font-medium',
            delta === 0 ? 'text-ink-3' : good ? 'text-ok' : 'text-alert',
          )}
        >
          {delta > 0 ? '+' : ''}
          {Math.round(delta * 100)}% rispetto al periodo prima
        </span>
      ) : null}
    </div>
  );
}
